/**
 * What the merchant changed between the customer's cart and the quote.
 *
 * The handoff requires the quote to show "every change the merchant made to the
 * cart (quantity, price, removed items) before the customer pays". Without it
 * the customer is asked to approve a total without being told a $3 item became
 * $5, or that two things are missing — which is exactly the moment trust is
 * won or lost.
 *
 * ## Matching
 *
 * `quote_items` has no foreign key back to `request_items`; the merchant
 * rebuilds the list and Laravel stores a free-text `description`. So lines are
 * matched on a normalised name. That is imperfect — a merchant who renames
 * "Rice 5lb" to "Riz 5 lb" produces a removal plus an addition rather than a
 * rename — but over-claiming a match would be worse: it would quietly hide a
 * substitution the customer needs to see.
 *
 * ## Money
 *
 * All amounts are integer HTG minor units, as stored. Nothing is converted or
 * rounded here; the customer-facing USD total comes from the server's quote.
 */
import type { QuoteItem, RequestItem } from './api';

export type ChangeKind = 'unchanged' | 'quantity' | 'price' | 'both' | 'added' | 'removed';

export interface QuoteLineChange {
  kind: ChangeKind;
  name: string;
  /** Present unless the line was added by the merchant. */
  before?: { quantity: number; unitHtgMinor: number; lineHtgMinor: number };
  /** Present unless the merchant removed the line. */
  after?: { quantity: number; unitHtgMinor: number; lineHtgMinor: number };
}

export interface QuoteDiff {
  changes: QuoteLineChange[];
  /** Lines the customer should actually look at. */
  changed: QuoteLineChange[];
  hasChanges: boolean;
  removedCount: number;
  addedCount: number;
  repricedCount: number;
}

/** Lowercased, collapsed whitespace — tolerant of casing and spacing only. */
function key(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Laravel serializes decimals as strings; a bad parse must not read as 0. */
function qty(raw: string | number): number {
  const n = typeof raw === 'number' ? raw : Number.parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

export function diffQuote(requestItems: RequestItem[], quoteItems: QuoteItem[]): QuoteDiff {
  const before = new Map<string, RequestItem>();
  for (const it of requestItems) before.set(key(it.name_snapshot), it);

  const changes: QuoteLineChange[] = [];
  const seen = new Set<string>();

  for (const q of quoteItems) {
    const k = key(q.description);
    seen.add(k);
    const orig = before.get(k);

    const after = {
      quantity: qty(q.quantity),
      unitHtgMinor: q.unit_htg_minor,
      lineHtgMinor: q.line_htg_minor,
    };

    if (!orig) {
      changes.push({ kind: 'added', name: q.description, after });
      continue;
    }

    const prior = {
      quantity: qty(orig.quantity),
      unitHtgMinor: orig.unit_price_htg_minor,
      lineHtgMinor: orig.line_total_htg_minor,
    };

    const qtyChanged = prior.quantity !== after.quantity;
    const priceChanged = prior.unitHtgMinor !== after.unitHtgMinor;

    const kind: ChangeKind =
      qtyChanged && priceChanged ? 'both'
      : qtyChanged ? 'quantity'
      : priceChanged ? 'price'
      : 'unchanged';

    changes.push({ kind, name: orig.name_snapshot, before: prior, after });
  }

  // Anything in the cart the quote never mentioned was dropped.
  for (const [k, orig] of before) {
    if (seen.has(k)) continue;
    changes.push({
      kind: 'removed',
      name: orig.name_snapshot,
      before: {
        quantity: qty(orig.quantity),
        unitHtgMinor: orig.unit_price_htg_minor,
        lineHtgMinor: orig.line_total_htg_minor,
      },
    });
  }

  const changed = changes.filter((c) => c.kind !== 'unchanged');

  return {
    changes,
    changed,
    hasChanges: changed.length > 0,
    removedCount: changed.filter((c) => c.kind === 'removed').length,
    addedCount: changed.filter((c) => c.kind === 'added').length,
    repricedCount: changed.filter((c) => c.kind === 'price' || c.kind === 'both').length,
  };
}
